import { useParams } from "react-router"
import { useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useFinancialModelSettingsStore } from "@/feature/financial-model/settings/store/financialModelSettingsStore.ts"
import { useGetDataGroupedByYear } from "@/lib/hooks/useGroupData"
import { usePageContext } from "@/lib/context/page"
import { useNavigationContext, YearNavigation } from "@/lib/context/navigation"
import { useFormatLocalizedNumber } from "@/lib/hooks/useLocaleFormatter"
import { TableErrorProvider } from "@/feature/financial-model/ecommerce/revenues/context/TableErrorContext.tsx"
import { Table, TableBody } from "@/components/ui/table.tsx"
import {
  GenericPeriodTableHeader,
  InputTypeProviderProvider,
  InputTypeSelector,
  PeriodTableRow,
} from "@/components/shared/tables"
import { TooltipInfo, TooltipProvider } from "@/components/ui/tooltip.tsx"
import { Show } from "@/components/shared/show.tsx"
import {
  MonthlyViewRowDataCells,
  YearlyViewRowDataCells,
} from "./direct-labour-view-data-cell.tsx"
import {
  DirectLabourColumnSwitchRequest,
  GetDirectLabourDataResponse,
} from "@/feature/financial-model/ecommerce/cogs/api/types.ts"
import { StandardInputType } from "@/types"
import { useSwitchDirectLabourRowTypeMutation } from "@/feature/financial-model/ecommerce/cogs/api"
import {
  TableContextValue,
  TableProvider,
} from "@/components/shared/tables/table-context.tsx"
import {
  PeriodFixedTableCell,
  PeriodRowHeader,
  PeriodTableCell,
  PeriodCellContent,
} from "@/components/shared/tables/table-cell.tsx"

type DirectLabourPeriod = GetDirectLabourDataResponse["periods"]

export const HistoricalDirectLabourTable = ({
  periods,
}: {
  periods: DirectLabourPeriod
}) => {
  const { id: financialModelId } = useParams()
  const financialModelSettings = useFinancialModelSettingsStore(
    (settings: { settings: Record<string, any> }) =>
      settings.settings[financialModelId!]
  )
  const currency = useMemo(() => {
    if (financialModelSettings?.currency?.functionalCurrency)
      return financialModelSettings?.currency?.functionalCurrency
    return `EUR`
  }, [financialModelSettings?.currency?.functionalCurrency])
  const { years, months, lastHistoricalPeriod, availableYears } =
    useGetDataGroupedByYear<DirectLabourPeriod[number]>(periods)
  const { state } = usePageContext()

  const { tableRef } = useNavigationContext()
  const { t } = useTranslation(`cogs`)
  const formatNumber = useFormatLocalizedNumber()

  const tableContext = useMemo(
    () =>
      ({
        lastHistoricalPeriod,
        view: state.view,
        queryPeriod: state.queryPeriod,
        period: state.period,
      }) satisfies TableContextValue,
    [lastHistoricalPeriod, state.view, state.queryPeriod, state.period]
  )
  return (
    <TooltipProvider>
      <TableProvider value={tableContext}>
        <TableErrorProvider>
          <YearNavigation
            view={state.view}
            years={availableYears}
            className="mt-6"
          />
          <Table className="overflow-auto mt-6" ref={tableRef}>
            <GenericPeriodTableHeader
              view={state.view}
              years={years}
              months={months}
            />
            <TableBody>
              <PeriodTableRow
                type={state.view === `monthly` ? `input` : `output`}
              >
                <PeriodFixedTableCell>
                  <PeriodRowHeader direction="horizontal">
                    <span className="pr-2">
                      {t(`cogs.periods.direct_labour.table.rows.total`)}
                    </span>
                    <TooltipInfo sideOffset={16}>
                      {t(`cogs.periods.direct_labour.table.tooltip.total`)}
                    </TooltipInfo>
                  </PeriodRowHeader>
                </PeriodFixedTableCell>
                <Show
                  when={state.view === `monthly`}
                  fallback={
                    <YearlyViewRowDataCells
                      periods={periods}
                      dataTarget={`total_fte`}
                    />
                  }
                >
                  <MonthlyViewRowDataCells
                    periods={periods}
                    dataTarget={`total_fte`}
                  />
                </Show>
              </PeriodTableRow>
              <PeriodTableRow
                type={state.view === `monthly` ? `input` : `output`}
              >
                <PeriodFixedTableCell>
                  <PeriodRowHeader direction="horizontal">
                    {t(`cogs.periods.direct_labour.table.rows.gross_salary`)}
                  </PeriodRowHeader>
                </PeriodFixedTableCell>
                <Show
                  when={state.view === `monthly`}
                  fallback={
                    <YearlyViewRowDataCells
                      periods={periods}
                      dataTarget={`avg_annual_gross_salary`}
                    />
                  }
                >
                  <MonthlyViewRowDataCells
                    periods={periods}
                    dataTarget={`avg_annual_gross_salary`}
                  />
                </Show>
              </PeriodTableRow>
              <PeriodTableRow
                type={state.view === `monthly` ? `input` : `output`}
              >
                <PeriodFixedTableCell>
                  <PeriodRowHeader direction="horizontal">
                    {t(`cogs.periods.direct_labour.table.rows.taxes_social`)}
                  </PeriodRowHeader>
                </PeriodFixedTableCell>
                <Show
                  when={state.view === `monthly`}
                  fallback={
                    <YearlyViewRowDataCells
                      periods={periods}
                      dataTarget={`social_contributions`}
                    />
                  }
                >
                  <MonthlyViewRowDataCells
                    periods={periods}
                    dataTarget={`social_contributions`}
                  />
                </Show>
              </PeriodTableRow>
              <PeriodTableRow
                type={state.view === `monthly` ? `input` : `output`}
              >
                <PeriodFixedTableCell>
                  <PeriodRowHeader direction="horizontal">
                    {t(`cogs.periods.direct_labour.table.rows.other_costs`)}
                  </PeriodRowHeader>
                </PeriodFixedTableCell>
                <Show
                  when={state.view === `monthly`}
                  fallback={
                    <YearlyViewRowDataCells
                      periods={periods}
                      dataTarget={`other_charges`}
                    />
                  }
                >
                  <MonthlyViewRowDataCells
                    periods={periods}
                    dataTarget={`other_charges`}
                  />
                </Show>
              </PeriodTableRow>
              <PeriodTableRow
                type={state.view === `monthly` ? `input` : `output`}
              >
                <PeriodFixedTableCell>
                  <PeriodRowHeader direction="horizontal">
                    {t(`cogs.periods.direct_labour.table.rows.bonus`)}
                  </PeriodRowHeader>
                </PeriodFixedTableCell>
                <Show
                  when={state.view === `monthly`}
                  fallback={
                    <YearlyViewRowDataCells
                      periods={periods}
                      dataTarget={`bonus`}
                    />
                  }
                >
                  <MonthlyViewRowDataCells
                    periods={periods}
                    dataTarget={`bonus`}
                  />
                </Show>
              </PeriodTableRow>
              <PeriodTableRow type="totals">
                <PeriodFixedTableCell>
                  <PeriodRowHeader direction="horizontal">
                    {t(`cogs.periods.direct_labour.table.rows.total_costs`)}
                  </PeriodRowHeader>
                </PeriodFixedTableCell>
                {periods.map((period) => (
                  <PeriodTableCell
                    key={period.id ?? period.period}
                    cell_period={period.period}
                  >
                    <PeriodCellContent>
                      {formatNumber(period.total_cost ?? 0, {
                        style: `currency`,
                        currency,
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </PeriodCellContent>
                  </PeriodTableCell>
                ))}
              </PeriodTableRow>
              <PeriodTableRow
                type={state.view === `monthly` ? `total-input` : `totals`}
              >
                <PeriodFixedTableCell>
                  <PeriodRowHeader direction="horizontal">
                    {t(
                      `cogs.periods.direct_labour.table.rows.percentage_of_direct_labour`
                    )}
                  </PeriodRowHeader>
                </PeriodFixedTableCell>
                <Show
                  when={state.view === `monthly`}
                  fallback={
                    <YearlyViewRowDataCells
                      periods={periods}
                      dataTarget={`percentage_of_direct_labour`}
                    />
                  }
                >
                  <MonthlyViewRowDataCells
                    periods={periods}
                    dataTarget={`percentage_of_direct_labour`}
                  />
                </Show>
              </PeriodTableRow>
              <PeriodTableRow type="totals">
                <PeriodFixedTableCell>
                  <PeriodRowHeader direction="horizontal">
                    <span className="pr-2">
                      {t(`cogs.periods.direct_labour.table.rows.direct_labour`)}
                    </span>
                    <TooltipInfo sideOffset={16}>
                      {t(
                        `cogs.periods.direct_labour.table.tooltip.direct_labour`
                      )}
                    </TooltipInfo>
                  </PeriodRowHeader>
                </PeriodFixedTableCell>
                {periods.map((period) => (
                  <PeriodTableCell
                    key={period.id ?? period.period}
                    cell_period={period.period}
                  >
                    <PeriodCellContent>
                      {formatNumber(period.direct_labour ?? 0, {
                        style: `currency`,
                        currency,
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </PeriodCellContent>
                  </PeriodTableCell>
                ))}
              </PeriodTableRow>
              <PeriodTableRow type="totals">
                <PeriodFixedTableCell>
                  <PeriodRowHeader direction="horizontal">
                    <span className="pr-2">
                      {t(
                        `cogs.periods.direct_labour.table.rows.indirect_labour`
                      )}
                    </span>
                    <TooltipInfo sideOffset={16}>
                      {t(
                        `cogs.periods.direct_labour.table.tooltip.indirect_labour`
                      )}
                    </TooltipInfo>
                  </PeriodRowHeader>
                </PeriodFixedTableCell>
                {periods.map((period) => (
                  <PeriodTableCell
                    key={period.id ?? period.period}
                    cell_period={period.period}
                  >
                    <PeriodCellContent>
                      {formatNumber(period.indirect_labour ?? 0, {
                        style: `currency`,
                        currency,
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </PeriodCellContent>
                  </PeriodTableCell>
                ))}
              </PeriodTableRow>
            </TableBody>
          </Table>
          {/* <div className="mt-4">
          <TableErrorMessage />
        </div> */}
        </TableErrorProvider>
      </TableProvider>
    </TooltipProvider>
  )
}

type ForecastDirectLabourTableProps = Pick<
  GetDirectLabourDataResponse,
  `config` | `periods`
>

export const ForecastDirectLabourTable = ({
  config,
  periods,
}: ForecastDirectLabourTableProps) => {
  const { state } = usePageContext()
  const { id: financialModelId } = useParams<{
    id: string
  }>()

  const { t } = useTranslation(`cogs`)
  const { months, years, lastHistoricalPeriod, availableYears } =
    useGetDataGroupedByYear<GetDirectLabourDataResponse["periods"][number]>(
      periods
    )
  const { tableRef } = useNavigationContext()
  const financialModelSettings = useFinancialModelSettingsStore(
    (settings: { settings: Record<string, any> }) =>
      settings.settings[financialModelId!]
  )
  const currency = useMemo(() => {
    if (financialModelSettings?.currency?.functionalCurrency)
      return financialModelSettings?.currency?.functionalCurrency
    return `EUR`
  }, [financialModelSettings?.currency?.functionalCurrency])
  const formatNumber = useFormatLocalizedNumber()
  const { mutate: switchPersonnelRowType, isPending: isSwitchingRowType } =
    useSwitchDirectLabourRowTypeMutation(financialModelId!)

  const onTypeChange = useCallback(
    (columnName: DirectLabourColumnSwitchRequest["column_name"]) =>
      (type: StandardInputType) => {
        switchPersonnelRowType({
          column_name: columnName,
          period_type: state.period,
          col_type: type,
        })
      },
    [state.period, switchPersonnelRowType]
  )

  const tableContext = useMemo(
    () =>
      ({
        lastHistoricalPeriod,
        view: state.view,
        queryPeriod: state.queryPeriod,
        period: state.period,
      }) satisfies TableContextValue,
    [lastHistoricalPeriod, state.view, state.queryPeriod, state.period]
  )

  return (
    <TooltipProvider>
      <TableProvider value={tableContext}>
        <TableErrorProvider>
          <YearNavigation
            view={state.view}
            years={availableYears}
            className="mt-6"
          />
          <Table className="overflow-auto mt-6" ref={tableRef}>
            <GenericPeriodTableHeader
              view={state.view}
              years={years}
              months={months}
            />
            <TableBody>
              <InputTypeProviderProvider defaultType={config.total_fte.type}>
                <PeriodTableRow
                  type={state.view === `monthly` ? `input` : `output`}
                >
                  <PeriodFixedTableCell>
                    <PeriodRowHeader direction="horizontal">
                      <span className="pr-2">
                        {t(`cogs.periods.direct_labour.table.rows.total`)}
                      </span>
                      <TooltipInfo sideOffset={16}>
                        {t(`cogs.periods.direct_labour.table.tooltip.total`)}
                      </TooltipInfo>
                    </PeriodRowHeader>
                    <InputTypeSelector
                      disabled={isSwitchingRowType || state.view !== `monthly`}
                      editable={state.view === `monthly`}
                      allowedTypes={config.total_fte.allowed_types}
                      onTypeChange={onTypeChange(`total_fte`)}
                    />
                  </PeriodFixedTableCell>
                  <Show
                    when={state.view === `monthly`}
                    fallback={
                      <YearlyViewRowDataCells
                        periods={periods}
                        dataTarget={`total_fte`}
                      />
                    }
                  >
                    <MonthlyViewRowDataCells
                      periods={periods}
                      dataTarget={`total_fte`}
                    />
                  </Show>
                </PeriodTableRow>
              </InputTypeProviderProvider>
              <InputTypeProviderProvider
                defaultType={config.avg_annual_gross_salary.type}
              >
                <PeriodTableRow
                  type={state.view === `monthly` ? `input` : `output`}
                >
                  <PeriodFixedTableCell>
                    <PeriodRowHeader direction="horizontal">
                      {t(`cogs.periods.direct_labour.table.rows.gross_salary`)}
                    </PeriodRowHeader>
                    <InputTypeSelector
                      disabled={isSwitchingRowType || state.view !== `monthly`}
                      editable={state.view === `monthly`}
                      allowedTypes={
                        config.avg_annual_gross_salary.allowed_types
                      }
                      onTypeChange={onTypeChange(`avg_annual_gross_salary`)}
                    />
                  </PeriodFixedTableCell>
                  <Show
                    when={state.view === `monthly`}
                    fallback={
                      <YearlyViewRowDataCells
                        periods={periods}
                        dataTarget={`avg_annual_gross_salary`}
                      />
                    }
                  >
                    <MonthlyViewRowDataCells
                      periods={periods}
                      dataTarget={`avg_annual_gross_salary`}
                    />
                  </Show>
                </PeriodTableRow>
              </InputTypeProviderProvider>
              <InputTypeProviderProvider
                defaultType={config.social_contributions.type}
              >
                <PeriodTableRow
                  type={state.view === `monthly` ? `input` : `output`}
                >
                  <PeriodFixedTableCell>
                    <PeriodRowHeader direction="horizontal">
                      {t(`cogs.periods.direct_labour.table.rows.taxes_social`)}
                    </PeriodRowHeader>
                    <InputTypeSelector
                      editable={state.view === `monthly`}
                      disabled={isSwitchingRowType || state.view !== `monthly`}
                      allowedTypes={config.social_contributions.allowed_types}
                      onTypeChange={onTypeChange(`social_contributions`)}
                    />
                  </PeriodFixedTableCell>
                  <Show
                    when={state.view === `monthly`}
                    fallback={
                      <YearlyViewRowDataCells
                        periods={periods}
                        dataTarget={`social_contributions`}
                      />
                    }
                  >
                    <MonthlyViewRowDataCells
                      periods={periods}
                      dataTarget={`social_contributions`}
                    />
                  </Show>
                </PeriodTableRow>
              </InputTypeProviderProvider>
              <InputTypeProviderProvider
                defaultType={config.other_charges.type}
              >
                <PeriodTableRow
                  type={state.view === `monthly` ? `input` : `output`}
                >
                  <PeriodFixedTableCell>
                    <PeriodRowHeader direction="horizontal">
                      {t(`cogs.periods.direct_labour.table.rows.other_costs`)}
                    </PeriodRowHeader>
                    <InputTypeSelector
                      disabled={state.view !== `monthly` || isSwitchingRowType}
                      editable={state.view === `monthly`}
                      allowedTypes={config.other_charges.allowed_types}
                      onTypeChange={onTypeChange(`other_charges`)}
                    />
                  </PeriodFixedTableCell>
                  <Show
                    when={state.view === `monthly`}
                    fallback={
                      <YearlyViewRowDataCells
                        periods={periods}
                        dataTarget={`other_charges`}
                      />
                    }
                  >
                    <MonthlyViewRowDataCells
                      periods={periods}
                      dataTarget={`other_charges`}
                    />
                  </Show>
                </PeriodTableRow>
              </InputTypeProviderProvider>
              <InputTypeProviderProvider defaultType={config.bonus.type}>
                <PeriodTableRow
                  type={state.view === `monthly` ? `input` : `output`}
                >
                  <PeriodFixedTableCell>
                    <PeriodRowHeader direction="horizontal">
                      {t(`cogs.periods.direct_labour.table.rows.bonus`)}
                    </PeriodRowHeader>
                    <InputTypeSelector
                      disabled={state.view !== `monthly` || isSwitchingRowType}
                      editable={state.view === `monthly`}
                      allowedTypes={config.bonus.allowed_types}
                      onTypeChange={onTypeChange(`bonus`)}
                    />
                  </PeriodFixedTableCell>
                  <Show
                    when={state.view === `monthly`}
                    fallback={
                      <YearlyViewRowDataCells
                        periods={periods}
                        dataTarget={`bonus`}
                      />
                    }
                  >
                    <MonthlyViewRowDataCells
                      periods={periods}
                      dataTarget={`bonus`}
                    />
                  </Show>
                </PeriodTableRow>
              </InputTypeProviderProvider>
              <PeriodTableRow type="totals">
                <PeriodFixedTableCell>
                  <PeriodRowHeader direction="horizontal">
                    {t(`cogs.periods.direct_labour.table.rows.total_costs`)}
                  </PeriodRowHeader>
                </PeriodFixedTableCell>
                {periods.map((period) => (
                  <PeriodTableCell
                    key={period.id ?? period.period}
                    cell_period={period.period}
                  >
                    <PeriodCellContent>
                      {formatNumber(period.total_cost ?? 0, {
                        style: `currency`,
                        currency,
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </PeriodCellContent>
                  </PeriodTableCell>
                ))}
              </PeriodTableRow>
              <InputTypeProviderProvider defaultType={`absolute`}>
                <PeriodTableRow
                  type={state.view === `monthly` ? `total-input` : `totals`}
                >
                  <PeriodFixedTableCell>
                    <PeriodRowHeader direction="horizontal">
                      {t(
                        `cogs.periods.direct_labour.table.rows.percentage_of_direct_labour`
                      )}
                    </PeriodRowHeader>
                    <InputTypeSelector
                      editable={state.view === `monthly`}
                      disabled
                      allowedTypes={[`absolute`]}
                      onTypeChange={onTypeChange(`social_contributions`)}
                    />
                  </PeriodFixedTableCell>
                  <Show
                    when={state.view === `monthly`}
                    fallback={
                      <YearlyViewRowDataCells
                        periods={periods}
                        dataTarget={`percentage_of_direct_labour`}
                      />
                    }
                  >
                    <MonthlyViewRowDataCells
                      periods={periods}
                      dataTarget={`percentage_of_direct_labour`}
                    />
                  </Show>
                </PeriodTableRow>
              </InputTypeProviderProvider>
              <PeriodTableRow type="totals">
                <PeriodFixedTableCell>
                  <PeriodRowHeader direction="horizontal">
                    <span className="pr-2">
                      {t(`cogs.periods.direct_labour.table.rows.direct_labour`)}
                    </span>
                    <TooltipInfo sideOffset={16}>
                      {t(
                        `cogs.periods.direct_labour.table.tooltip.direct_labour`
                      )}
                    </TooltipInfo>
                  </PeriodRowHeader>
                </PeriodFixedTableCell>
                {periods.map((period) => (
                  <PeriodTableCell
                    key={period.id ?? period.period}
                    cell_period={period.period}
                  >
                    <PeriodCellContent>
                      {formatNumber(period.direct_labour ?? 0, {
                        style: `currency`,
                        currency,
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </PeriodCellContent>
                  </PeriodTableCell>
                ))}
              </PeriodTableRow>
              <PeriodTableRow type="totals">
                <PeriodFixedTableCell>
                  <PeriodRowHeader direction="horizontal">
                    <span className="pr-2">
                      {t(
                        `cogs.periods.direct_labour.table.rows.indirect_labour`
                      )}
                    </span>
                    <TooltipInfo sideOffset={16}>
                      {t(
                        `cogs.periods.direct_labour.table.tooltip.indirect_labour`
                      )}
                    </TooltipInfo>
                  </PeriodRowHeader>
                </PeriodFixedTableCell>
                {periods.map((period) => (
                  <PeriodTableCell
                    key={period.id ?? period.period}
                    cell_period={period.period}
                  >
                    <PeriodCellContent>
                      {formatNumber(period.indirect_labour ?? 0, {
                        style: `currency`,
                        currency,
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </PeriodCellContent>
                  </PeriodTableCell>
                ))}
              </PeriodTableRow>
            </TableBody>
          </Table>
          {/* <div className="mt-4">
            <TableErrorMessage />
          </div> */}
        </TableErrorProvider>
      </TableProvider>
    </TooltipProvider>
  )
}
